import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { friendlyErrorMessage } from '../../../shared/friendlyError';
import { identityGateway } from '../services';
import { ApiError, type MembershipResponse, type RoleResponse } from '../types';

/** EPN-78 — lists company members and lets an authorized caller change a member's role or revoke them.
 * There is deliberately NO "add member" form here: the only direct-add API takes an Admin-chosen initial
 * password, which conflicts with the approved secure-invitation direction, and no authenticated
 * active-company invitation API exists yet (BACKEND GAP — see docs/roles-permissions-user-access.md).
 * Nothing here fakes an invitation.
 *
 * This app has no client-side permission list anywhere (confirmed during the precheck: the JWT
 * carries no `permissions` array the frontend can read ahead of time), so "can the signed-in user manage
 * members/roles" is discovered reactively: attempt the action, treat a 403 as authoritative proof, then
 * switch to a sticky read-only view for the rest of this page's lifetime — the same pattern established in
 * EPN-80's Billing Management page. */
export function CompanyMembersPage({ companyId }: { companyId: string }) {
  const [members, setMembers] = useState<MembershipResponse[]>([]);
  const [roles, setRoles] = useState<RoleResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isReadOnly, setIsReadOnly] = useState(false);

  const [changingRoleFor, setChangingRoleFor] = useState<string | null>(null);
  const [pendingRoleId, setPendingRoleId] = useState('');
  const [busyMembershipId, setBusyMembershipId] = useState<string | null>(null);

  // useCallback (keyed on companyId) rather than a plain function so the effect below can list it as
  // a real dependency instead of suppressing exhaustive-deps — the identity only changes when
  // companyId does, so this doesn't re-run on every render.
  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [memberList, roleList] = await Promise.all([
        identityGateway.listMembers(companyId),
        identityGateway.listRoles(),
      ]);
      setMembers(memberList);
      setRoles(roleList);
    } catch (err) {
      setError(friendlyErrorMessage(err, 'Could not load members.'));
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  // SuperAdmin is an internal/platform role (flagged `isSystemRole` by the roles catalog) that the server
  // never lets a company membership action assign — so it is never offered here. It stays in the catalog
  // for the read-only Roles & Permissions view only.
  const assignableRoles = useMemo(() => roles.filter((r) => !r.isSystemRole), [roles]);

  useEffect(() => {
    // `load` clears any stale error before re-fetching — the standard "fetch on mount/prop change"
    // pattern React's own docs still endorse for a case this simple; the stricter set-state-in-effect
    // rule is disabled here deliberately, not overlooked.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void load();
  }, [load]);

  /** Treats a 403 as authoritative proof the signed-in role cannot manage members, switching to the sticky
   * read-only view. A 404 means the member is already gone (revoked or changed by someone else), so the list is
   * refreshed and the generated request-path text is never shown. Every other failure is a normal, retryable
   * error: a 400 shows the server's own validation message (for example the last-Owner rule), anything else
   * shows safe copy — never a raw exception message or stack. */
  async function handleManagementError(err: unknown, fallback: string) {
    if (err instanceof ApiError && err.status === 403) {
      setIsReadOnly(true);
      setError('Your role does not have permission to manage company members.');
      return;
    }
    if (err instanceof ApiError && err.status === 404) {
      setChangingRoleFor(null);
      await load();
      setError('That member no longer exists or was already removed. The list has been refreshed.');
      return;
    }
    setError(friendlyErrorMessage(err, fallback));
  }

  async function handleRevoke(membershipId: string) {
    setError(null);
    setBusyMembershipId(membershipId);
    try {
      await identityGateway.revokeMember(companyId, membershipId);
      await load();
    } catch (err) {
      await handleManagementError(err, 'Could not revoke membership.');
    } finally {
      setBusyMembershipId(null);
    }
  }

  function startChangeRole(member: MembershipResponse) {
    setChangingRoleFor(member.id);
    // A member who currently holds a non-assignable (system) role has no matching option — start empty so
    // the control never silently shows a different role than the one that would be saved.
    setPendingRoleId(assignableRoles.some((r) => r.id === member.roleId) ? member.roleId : '');
  }

  async function handleChangeRole(e: FormEvent) {
    e.preventDefault();
    if (!changingRoleFor) return;
    setError(null);
    setBusyMembershipId(changingRoleFor);
    try {
      await identityGateway.changeMembershipRole(companyId, changingRoleFor, pendingRoleId);
      setChangingRoleFor(null);
      await load();
    } catch (err) {
      await handleManagementError(err, 'Could not change this member’s role.');
    } finally {
      setBusyMembershipId(null);
    }
  }

  return (
    <section className="card" aria-labelledby="company-members-heading">
      <h2 id="company-members-heading">Members</h2>
      {error && <p className={isReadOnly ? 'muted' : 'error'} role="alert">{error}</p>}
      {loading && <p className="muted" role="status">Loading members…</p>}

      {!loading && (
        <table aria-label="Company members">
          <thead>
            <tr>
              <th>Email</th>
              <th>Role</th>
              {!isReadOnly && <th></th>}
            </tr>
          </thead>
          <tbody>
            {members.map((m) => (
              <tr key={m.id}>
                <td>{m.email}</td>
                <td>
                  {changingRoleFor === m.id ? (
                    <form className="inline-form" onSubmit={handleChangeRole}>
                      <select
                        aria-label={`New role for ${m.email}`}
                        value={pendingRoleId}
                        onChange={(e) => setPendingRoleId(e.target.value)}
                      >
                        {pendingRoleId === '' && <option value="" disabled>Select a role…</option>}
                        {assignableRoles.map((r) => (
                          <option key={r.id} value={r.id}>{r.name}</option>
                        ))}
                      </select>
                      <button type="submit" disabled={busyMembershipId === m.id || pendingRoleId === ''}>
                        {busyMembershipId === m.id ? 'Saving…' : 'Save'}
                      </button>
                      <button type="button" className="link" onClick={() => setChangingRoleFor(null)}>Cancel</button>
                    </form>
                  ) : (
                    m.roleName
                  )}
                </td>
                {!isReadOnly && (
                  <td>
                    {changingRoleFor !== m.id && (
                      <button type="button" className="link" onClick={() => startChangeRole(m)}>
                        Change role
                      </button>
                    )}
                    <button
                      type="button"
                      className="link"
                      disabled={busyMembershipId === m.id}
                      onClick={() => void handleRevoke(m.id)}
                    >
                      Revoke
                    </button>
                  </td>
                )}
              </tr>
            ))}
            {!loading && members.length === 0 && (
              <tr><td colSpan={isReadOnly ? 2 : 3} className="muted">No members yet.</td></tr>
            )}
          </tbody>
        </table>
      )}

      {!loading && (
        <p className="muted" data-testid="invite-not-available">
          Inviting new members from this screen is not available yet. A secure, expiring invitation flow is
          required first; until then, members can be listed, have their role changed, or be revoked here.
        </p>
      )}
    </section>
  );
}
